export const FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX = 'fc_override_';
export const FRONTEND_CONFIG_OVERRIDE_MAX_AGE_SECONDS = 86400;

export type FrontendConfigType = 'Boolean' | 'String' | 'Number' | 'StringCsv' | 'NumberCsv';

export type FrontendConfigValueMap = {
  Boolean: boolean;
  String: string;
  Number: number;
  StringCsv: Array<string>;
  NumberCsv: Array<number>;
};

export type FrontendConfigValue<T extends FrontendConfigType> = FrontendConfigValueMap[T];

export type FrontendConfigOverride = {
  [T in FrontendConfigType]: {
    type: T;
    name: string;
    value: FrontendConfigValue<T>;
  };
}[FrontendConfigType];

const COOKIE_TYPE_NAMES: Record<FrontendConfigType, string> = {
  Boolean: 'boolean',
  String: 'string',
  Number: 'number',
  StringCsv: 'string-csv',
  NumberCsv: 'number-csv',
};

const CONFIG_TYPES: Array<FrontendConfigType> = ['Boolean', 'StringCsv', 'NumberCsv', 'String', 'Number'];
const LIST_ENCODING_PREFIX = 'json:';

export function getFrontendConfigOverrideCookieName(type: FrontendConfigType, name: string): string {
  return `${FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX}${COOKIE_TYPE_NAMES[type]}_${name}`;
}

export function serializeFrontendConfigOverride<T extends FrontendConfigType>(
  type: T,
  value: FrontendConfigValue<T>,
): string {
  const rawValue = Array.isArray(value) ? `${LIST_ENCODING_PREFIX}${JSON.stringify(value)}` : String(value);
  return encodeURIComponent(rawValue);
}

export function parseFrontendConfigOverride<T extends FrontendConfigType>(
  type: T,
  encodedValue: string,
): FrontendConfigValue<T> | undefined {
  let value: string;
  try {
    value = decodeURIComponent(encodedValue);
  } catch {
    return undefined;
  }

  if (type === 'Boolean') {
    return (value === 'true' ? true : value === 'false' ? false : undefined) as FrontendConfigValue<T> | undefined;
  }

  if (type === 'Number') {
    const numberValue = value.trim() === '' ? Number.NaN : Number(value);
    return (Number.isFinite(numberValue) ? numberValue : undefined) as FrontendConfigValue<T> | undefined;
  }

  if (type === 'String') {
    return value as FrontendConfigValue<T>;
  }

  if (value.startsWith(LIST_ENCODING_PREFIX)) {
    try {
      const list = JSON.parse(value.slice(LIST_ENCODING_PREFIX.length)) as unknown;
      if (!Array.isArray(list)) {
        return undefined;
      }
      if (type === 'StringCsv') {
        return (list.every((item) => typeof item === 'string') ? list : undefined) as
          | FrontendConfigValue<T>
          | undefined;
      }
      return (list.every((item) => typeof item === 'number' && Number.isFinite(item)) ? list : undefined) as
        | FrontendConfigValue<T>
        | undefined;
    } catch {
      return undefined;
    }
  }

  if (type === 'StringCsv') {
    return value.split(',') as FrontendConfigValue<T>;
  }

  const numberValues = value.split(',').map((item) => (item.trim() === '' ? Number.NaN : Number(item)));
  return (numberValues.every(Number.isFinite) ? numberValues : undefined) as FrontendConfigValue<T> | undefined;
}

export function parseFrontendConfigOverrideCookie(
  cookieName: string,
  cookieValue: string,
): FrontendConfigOverride | undefined {
  for (const type of CONFIG_TYPES) {
    const prefix = getFrontendConfigOverrideCookieName(type, '');
    if (cookieName.startsWith(prefix)) {
      const name = cookieName.slice(prefix.length);
      const value = parseFrontendConfigOverride(type, cookieValue);
      return value === undefined ? undefined : ({ type, name, value } as FrontendConfigOverride);
    }
  }

  if (cookieName.startsWith(FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX)) {
    const value = parseFrontendConfigOverride('Boolean', cookieValue);
    return value === undefined
      ? undefined
      : {
          type: 'Boolean',
          name: cookieName.slice(FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX.length),
          value,
        };
  }

  return undefined;
}

function getCookieValue(name: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const cookie = document.cookie.split(';').find((item) => item.trim().startsWith(`${name}=`));
  return cookie?.trim().slice(name.length + 1);
}

export function getFrontendConfigOverride<T extends FrontendConfigType>(
  type: T,
  name: string,
): FrontendConfigValue<T> | undefined {
  const typedValue = getCookieValue(getFrontendConfigOverrideCookieName(type, name));
  if (typedValue !== undefined) {
    const override = parseFrontendConfigOverride(type, typedValue);
    if (override !== undefined) {
      return override;
    }
  }

  if (type === 'Boolean') {
    const legacyValue = getCookieValue(`${FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX}${name}`);
    if (legacyValue !== undefined) {
      return parseFrontendConfigOverride(type, legacyValue);
    }
  }

  return undefined;
}
